import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineRefresh } from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { RiCoinLine } from 'react-icons/ri';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { getWalletTokenBalances } from '../services/wallet';
import NumberInput, { parseNumberInput } from '../components/NumberInput';
import { Skeleton, WalletSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';
import PancakeSwapGuide from '../components/PancakeSwapGuide';

export default function WalletPage() {
  const { user, config, openConnect, connectOpening, refreshUser, address, loading } = useWallet();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [amount, setAmount] = useState('');
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [chainBusy, setChainBusy] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [usdtBal, setUsdtBal] = useState(null);
  const [dfiBal, setDfiBal] = useState(null);

  const loadClaims = useCallback(async () => {
    const data = await api.myClaims();
    setClaims(data.claims || []);
  }, []);

  const loadChainBalances = useCallback(async () => {
    if (!address || !config?.usdt_contract) return;
    setChainBusy(true);
    try {
      const { usdt, dfi } = await getWalletTokenBalances({
        usdtContract: config.usdt_contract,
        dfiContract: config.dfi_contract,
        ownerAddress: address,
      });
      setUsdtBal(usdt);
      setDfiBal(dfi);
    } catch (e) {
      const raw = String(e?.shortMessage || e?.message || '');
      const message = e?.code === 'CALL_EXCEPTION' || /missing revert data|CALL_EXCEPTION/i.test(raw)
        ? 'Could not read token balances on BNB Smart Chain.'
        : (raw || 'Failed to load on-chain balances');
      toastRef.current.error(message);
    } finally {
      setChainBusy(false);
    }
  }, [address, config?.usdt_contract, config?.dfi_contract]);

  useEffect(() => {
    if (loading) return undefined;
    if (!user?.wallet_address) {
      setPageLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPageLoading(true);
    (async () => {
      try {
        await Promise.all([refreshUser(), loadClaims().catch(() => {}), loadChainBalances()]);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, loading, loadClaims, loadChainBalances, refreshUser]);

  useEffect(() => {
    if (!user?.wallet_address) return undefined;
    function syncBalance() {
      refreshUser();
    }
    function onVisible() {
      if (document.visibilityState === 'visible') syncBalance();
    }
    window.addEventListener('focus', syncBalance);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', syncBalance);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.wallet_address, refreshUser]);

  async function claimAmount(value) {
    setBusy(true);
    try {
      const data = await api.claim(value);
      const claim = data.claim || {};
      toast.success(
        claim.status === 'completed'
          ? `DFI sent. Tx ${claim.tx_hash?.slice(0, 10)}…`
          : `Claim status: ${claim.status || 'unknown'}`
      );
      await refreshUser();
      await loadClaims();
      await loadChainBalances();
      setAmount('');
    } catch (err) {
      toast.error(err.message || 'Claim failed');
    } finally {
      setBusy(false);
    }
  }

  async function onClaim(e) {
    e.preventDefault();
    const value = parseNumberInput(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a valid claim amount');
      return;
    }
    await claimAmount(value);
  }

  async function onClaimAll() {
    const available = Number(user?.virtual_balance || 0);
    const minClaim = Number(config?.claim_min_amount || 10);
    if (available < minClaim) {
      toast.error(`Minimum claim amount is ${minClaim}`);
      return;
    }
    await claimAmount(available);
  }

  if (loading || (user && pageLoading)) {
    return <WalletSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Wallet" subtitle="Balances and DFI claims" />
        <motion.div
          className="panel panel--glow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Wallet</div>
          <h2>Connect to view balance & claim</h2>
          <p className="muted">Account balance and DFI claims require a connected wallet.</p>
          <div className="stack">
            <button
              className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
              type="button"
              onClick={() => openConnect()}
              disabled={connectOpening}
              aria-busy={connectOpening}
            >
              {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
              {connectOpening ? 'Connecting…' : 'Connect Wallet'}
            </button>
          </div>
        </motion.div>
        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          <PancakeSwapGuide config={config} variant="panel" />
        </motion.section>
      </>
    );
  }

  const rate = Number(config?.dfi_per_usdt || 1);
  const minClaim = Number(config?.claim_min_amount || 10);
  const chargePercent = Number(config?.claim_charge_percent ?? 10);
  const unclaimed = Number(user.virtual_balance || 0);
  const unclaimedCharge = Number((((Number.isFinite(chargePercent) ? chargePercent : 0) * unclaimed) / 100).toFixed(8));
  const unclaimedNet = Number(Math.max(0, unclaimed - unclaimedCharge).toFixed(8));
  const unclaimedDfi = unclaimedNet * rate;
  const claimPreview = parseNumberInput(amount);
  const previewCharge = Number.isFinite(claimPreview)
    ? Number((((Number.isFinite(chargePercent) ? chargePercent : 0) * claimPreview) / 100).toFixed(8))
    : 0;
  const previewNet = Number.isFinite(claimPreview) ? Number(Math.max(0, claimPreview - previewCharge).toFixed(8)) : 0;
  const previewDfi = previewNet * rate;

  return (
    <>
      <PageHeader title="Wallet" subtitle="Balances and DFI claims" />
      <motion.section
        className="panel panel--glow panel--tint-cyan"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="eyebrow">On-chain balances</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>Connected wallet</h2>
          <button
            className="btn btn--ghost"
            type="button"
            onClick={() => {
              refreshUser();
              loadChainBalances();
            }}
            disabled={chainBusy}
          >
            <HiOutlineRefresh />
            {chainBusy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="wallet-balances">
          <div className="stat">
            <span>USDT balance</span>
            <strong>
              {chainBusy && !usdtBal ? (
                <Skeleton width="5rem" height="1.35rem" />
              ) : (
                <>
                  {usdtBal ? Number(usdtBal.formatted).toFixed(4) : '—'}
                  <em>USDT</em>
                </>
              )}
            </strong>
          </div>
          <div className="stat">
            <span>DFI balance</span>
            <strong>
              {chainBusy && !dfiBal ? (
                <Skeleton width="5rem" height="1.35rem" />
              ) : (
                <>
                  {dfiBal ? Number(dfiBal.formatted).toFixed(4) : '—'}
                  <em>DFI</em>
                </>
              )}
            </strong>
          </div>
        </div>
      </motion.section>

      <div className="panel-grid panel-grid--span-last">
        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
        >
          <div className="eyebrow">Unclaimed</div>
          <h2 style={{ marginBottom: '0.25rem' }}>Ready to claim</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Daily earnings credit this balance. Claiming sends DFI to your connected wallet after the claim charge.
          </p>
          <div className="wallet-balances">
            <div className="stat">
              <span>Unclaimed credit</span>
              <strong>{unclaimed.toFixed(4)}</strong>
            </div>
            <div className="stat">
              <span>Charge ({Number.isFinite(chargePercent) ? chargePercent : 0}%)</span>
              <strong>{unclaimedCharge.toFixed(4)}</strong>
            </div>
            <div className="stat">
              <span>You receive</span>
              <strong>
                {unclaimedDfi.toFixed(4)}
                <em>DFI</em>
              </strong>
            </div>
          </div>
          <div className="stack">
            <motion.button
              className="btn btn--primary"
              type="button"
              disabled={busy || unclaimed < minClaim}
              onClick={onClaimAll}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RiCoinLine />
              {busy ? 'Claiming…' : `Claim ${unclaimedDfi.toFixed(4)} DFI`}
            </motion.button>
          </div>
        </motion.section>

        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3>
            <RiCoinLine style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan)' }} />
            Custom claim
          </h3>
          <form onSubmit={onClaim}>
            <div className="field">
              <label htmlFor="claim">Amount (min {minClaim})</label>
              <NumberInput
                id="claim"
                value={amount}
                onChange={setAmount}
                allowDecimal
                maxDecimals={8}
                placeholder="0.00"
                required
              />
              <span className="muted">
                {Number.isFinite(claimPreview)
                  ? `Charge ${Number.isFinite(chargePercent) ? chargePercent : 0}% (${previewCharge.toFixed(4)}) · you receive ~${previewDfi.toFixed(4)} DFI`
                  : `Charge ${Number.isFinite(chargePercent) ? chargePercent : 0}% · enter an amount to preview DFI`}
              </span>
            </div>
            <motion.button
              className="btn btn--primary"
              type="submit"
              disabled={busy}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RiCoinLine />
              {busy ? 'Claiming…' : 'Claim DFI'}
            </motion.button>
          </form>
        </motion.section>

        <motion.section
          className="panel panel--full"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
        >
          <PancakeSwapGuide config={config} variant="panel" />
        </motion.section>

        <motion.section
          className="panel"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
        >
          <h3>Claim history</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Amount</th>
                  <th>Charge</th>
                  <th>DFI</th>
                  <th>Status</th>
                  <th>Tx</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => (
                  <tr key={c.id}>
                    <td>{Number(c.amount_virtual).toFixed(4)}</td>
                    <td>
                      {Number(c.charge_amount || 0).toFixed(4)}
                      {Number(c.charge_percent) > 0 ? (
                        <span className="muted" style={{ display: 'block', fontSize: '0.72rem' }}>
                          {Number(c.charge_percent)}%
                        </span>
                      ) : null}
                    </td>
                    <td>{Number(c.amount_dfi).toFixed(4)}</td>
                    <td>
                      <span className={`badge${c.status !== 'completed' ? ' badge--pending' : ''}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>
                      {c.tx_hash ? (
                        <a
                          href={`https://bscscan.com/tx/${c.tx_hash}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {c.tx_hash.slice(0, 10)}…
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {!claims.length ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      No claims yet
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </motion.section>
      </div>
    </>
  );
}
